import { useLazyQuery } from '@apollo/client';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { AccessEvent, MobileOpCode, Query, QueryGetAccessEventsForTenantArgs } from 'graphql/generated/graphqlTypes';
import { GET_ACCESS_EVENTS_FOR_TENANT } from 'graphql/v2/queries';
import { useEffect, useMemo, useState } from 'react';
import { TranslatedHookProps } from 'v2/hooks/hooks.types';

dayjs.extend(utc);

export const LAST_WEEK_DAYS = 7;

export interface DailyOpeningCount {
  /** Start of the day this bucket covers, as an ISO string — format it with `formatDate`. */
  date: string;
  count: number;
}

// the window covers LAST_WEEK_DAYS days ending today, so it starts LAST_WEEK_DAYS - 1 days ago
const getLastWeekStartOfDay = () =>
  dayjs()
    .startOf('day')
    .subtract(LAST_WEEK_DAYS - 1, 'day');

// fixed at effect start so every page of the loop below queries the exact same window
const getLastWeekDateRange = () => ({
  rangeStart: getLastWeekStartOfDay().utc().format(),
  rangeEnd: dayjs().endOf('day').utc().format()
});

const useWeeklyDoorOpenings = ({ handleFetchError, translation }: TranslatedHookProps) => {
  const [accessEvents, setAccessEvents] = useState<AccessEvent[]>([]);
  const [loading, setLoading] = useState(true);

  const [getAccessEvents] = useLazyQuery<Pick<Query, 'getAccessEventsForTenant'>, QueryGetAccessEventsForTenantArgs>(
    GET_ACCESS_EVENTS_FOR_TENANT
  );

  useEffect(() => {
    (async () => {
      setLoading(true);

      const dateRange = getLastWeekDateRange();
      const items: AccessEvent[] = [];
      let nextToken: string | null | undefined;

      // no `limit` is passed on purpose, so each page comes back as large as the API allows —
      // that keeps the number of round trips (and therefore the total fetch time) as low as possible
      do {
        const { data, error } = await getAccessEvents({
          variables: {
            nextToken: nextToken ?? undefined,
            filter: {
              actions: [MobileOpCode.AccessGranted],
              dateRange
            }
          }
        });

        if (error) {
          handleFetchError(translation.access_events_error_fetching);
          setLoading(false);
          return;
        }

        if (!data) break;

        items.push(...data.getAccessEventsForTenant.items);
        nextToken = data.getAccessEventsForTenant.nextToken;
      } while (nextToken);

      setAccessEvents(items);
      setLoading(false);
    })();
  }, []);

  // one bucket per day, oldest first, so empty days still render as a zero-height bar
  const dailyOpenings = useMemo<DailyOpeningCount[]>(() => {
    const windowStart = getLastWeekStartOfDay();
    const buckets = Array.from({ length: LAST_WEEK_DAYS }, (_, index) => ({
      date: windowStart.add(index, 'day'),
      count: 0
    }));

    accessEvents.forEach(({ createdAt }) => {
      const dayOffset = dayjs(createdAt).startOf('day').diff(windowStart, 'day');
      // events outside the window (e.g. timezone edge) are ignored rather than miscounted
      if (dayOffset < 0 || dayOffset >= LAST_WEEK_DAYS) return;

      buckets[dayOffset].count += 1;
    });

    return buckets.map(({ date, count }) => ({ date: date.format(), count }));
  }, [accessEvents]);

  const totalOpenings = useMemo(() => dailyOpenings.reduce((sum, { count }) => sum + count, 0), [dailyOpenings]);

  const averageOpeningsPerDay = useMemo(() => Math.round(totalOpenings / LAST_WEEK_DAYS), [totalOpenings]);

  // undefined while there is nothing to show, so the caller can render an empty state instead of a 0 peak
  const peakDay = useMemo<DailyOpeningCount | undefined>(() => {
    if (totalOpenings === 0) return undefined;

    return dailyOpenings.reduce((peak, day) => (day.count > peak.count ? day : peak));
  }, [dailyOpenings, totalOpenings]);

  return {
    dailyOpenings,
    totalOpenings,
    averageOpeningsPerDay,
    peakDay,
    loading
  };
};

export default useWeeklyDoorOpenings;
